import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  CardNotification,
  Drawer,
  Heading,
  Link,
  Loading,
  RadioButton,
  Tag,
  Text,
  TextField,
  toast,
  Tooltip,
} from "@foris/avocado-suite";
import { useLayoutStore } from "@context/useLayoutStore";
import {
  useCreateOrDeleteReferences,
  useGetReferencesCategories,
} from "@services/collections/references/referencesServices";
import styles from "./groupReferencesDrawer.module.scss";
import { ExternalReference, Reference, ReferenceCategory } from "@models/ISchema";
import { emitEvent } from "@common/hooks/useListenEvent";
import GroupExternalCrnSelector from "../group-external-crn-selector";
import { FREE_TEXT_MODALITY_CODE } from "@services/constants/references";
import { getAdaptedReferences } from "@services/collections/references/referencesAdapters";
import { useTranslation } from "react-i18next";
import Modal from "@components/modal";
import { isLaboratoryCourseType } from "@utils/courseUtils";
import { isHermeslabCategoryCode } from "@utils/externalReferences";

interface GroupReferencesDrawerProps {
  groupId: string;
  termCode: string;
  isOpen: boolean;
  scenario: string;
  origin: string;
  courseType?: string | null;
  references?: Partial<Reference>[];
  onClose: () => void;
}

/**
 * Hermeslab references are only allowed on laboratory groups (CHS-2974). Every other
 * modality keeps its availability, and clients without a Hermeslab category are untouched.
 */
const isModalityDisabled = (category: ReferenceCategory, isLaboratoryGroup: boolean) =>
  isHermeslabCategoryCode(category?.code) && !isLaboratoryGroup;

const GroupReferencesDrawer: React.FC<GroupReferencesDrawerProps> = ({
  groupId,
  scenario,
  origin,
  isOpen,
  courseType,
  references = [],
  termCode,
  onClose,
}) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const [selectedModality, setSelectedModality] = useState<ReferenceCategory | null>(null);
  const [newReferences, setNewReferences] = useState<
    Partial<Reference & { isNew?: boolean; customLabel?: string }>[]
  >([]);
  const [freeTextReference, setFreeTextReference] = useState<string>("");
  const [categoryToDelete, setCategoryToDelete] = useState<ReferenceCategory | null>(null);
  const isLaboratoryGroup = isLaboratoryCourseType(courseType);

  const { referenceCategories, getReferencesCategories } = useGetReferencesCategories({
    onCompleted: data => {
      if (data?.items?.length) {
        const freeTextCategory = data?.items?.find(
          category => category?.code === "base" && category?.enabled,
        );
        const firstAvailableCategory = data?.items?.find(
          category => !isModalityDisabled(category, isLaboratoryGroup),
        );

        setSelectedModality(freeTextCategory ?? firstAvailableCategory ?? null);
      }
    },
  });

  const { handleCreateOrDeleteReferences, loading: isCreatingOrDeletingReferences } =
    useCreateOrDeleteReferences({
      onCompleted: data => {
        if (data?.commited) {
          toast.success(t("group-references.notifications.success-save"), {
            autoClose: 3000,
          });

          emitEvent("on:refresh", { target: "group-calendar" });
          onClose();
        }
      },
    });

  const hasChanges = useMemo(() => {
    if (references?.length !== newReferences?.length) {
      return true;
    }

    const hasNewReferences = newReferences?.find(ref => !ref?.id);

    if (hasNewReferences) {
      return true;
    }

    return false;
  }, [references, newReferences]);

  const hasHermesOrHermeslabReference = useMemo(() => {
    const hasReferenceWithFreeText = [...newReferences]?.filter(ref => {
      const lowerCaseValue = ref?.value?.toLowerCase();
      return (
        lowerCaseValue?.includes("hermes") ||
        lowerCaseValue?.includes("hermeslab") ||
        ref?.referenceCategory?.code !== FREE_TEXT_MODALITY_CODE
      );
    });

    return {
      hasReference: !!hasReferenceWithFreeText?.length,
      isFreeText: hasReferenceWithFreeText?.some(
        ref => ref?.referenceCategory?.code === FREE_TEXT_MODALITY_CODE,
      ),
    };
  }, [newReferences]);

  const referencesByCategory = useMemo(() => {
    const values: {
      values: Partial<Reference & { customLabel?: string; isNew?: boolean }>[];
      category: ReferenceCategory;
    }[] = [];

    newReferences?.forEach(ref => {
      if (ref?.referenceCategory?.code in values) {
        values[ref?.referenceCategory?.code].values.push(ref);
      } else {
        values[ref?.referenceCategory?.code] = {
          values: [ref],
          category: ref?.referenceCategory,
        };
      }
    });

    return Object.values(values);
  }, [newReferences]);

  useEffect(() => {
    if (isOpen) {
      setNewReferences([...getAdaptedReferences(references)]);

      if (!referenceCategories?.length && scenario && origin) {
        getReferencesCategories({ scenarioId: scenario, originId: origin });
      }
    }
  }, [isOpen]);

  const handleSaveChanges = useCallback(() => {
    if (!hasChanges || hasHermesOrHermeslabReference.isFreeText) {
      return;
    }

    const referencesByCategory = newReferences?.reduce<{
      [key: string]: { values: string[]; categoryCode: string };
    }>((acc, ref) => {
      if (ref?.referenceCategory?.code in acc) {
        acc[ref?.referenceCategory?.code].values.push(ref?.value);
      } else {
        acc[ref?.referenceCategory?.code] = {
          values: [ref?.value],
          categoryCode: ref?.referenceCategory?.code,
        };
      }

      return acc;
    }, {});

    handleCreateOrDeleteReferences({
      scenarioId: scenario,
      originId: origin,
      references: Object.values(referencesByCategory),
      groupId,
    });
  }, [hasChanges, isCreatingOrDeletingReferences, groupId, scenario, origin, newReferences]);

  const handleSaveReferenceFromTextfield = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        const value = e.currentTarget.value.trim();
        const isNotSaved = !newReferences?.find(
          ref => ref?.value === value && ref?.referenceCategory?.code === FREE_TEXT_MODALITY_CODE,
        );

        if (!!value && isNotSaved) {
          setNewReferences(prev => [
            ...prev,
            {
              value,
              referenceCategoryId: selectedModality?.id,
              referenceCategory: selectedModality,
              isNew: true,
            },
          ]);

          setFreeTextReference("");
        }
      }
    },
    [selectedModality, newReferences],
  );

  const handleSaveReferenceFromGroupExternalCrnSelector = useCallback(
    (reference: ExternalReference) => {
      const newRef = {
        value: reference?.referenceKey,
        customLabel: `${selectedModality?.code}: ${reference?.referenceKey}`,
        referenceCategoryId: selectedModality?.id,
        referenceCategory: selectedModality,
        isNew: true,
      };

      setNewReferences(prev => [...prev, newRef]);
    },
    [newReferences, selectedModality],
  );

  const handleDeleteAllReferences = useCallback(
    (category: ReferenceCategory) => {
      const updatedReferences = newReferences?.filter(
        ref => ref?.referenceCategory?.id !== category?.id,
      );

      setNewReferences(updatedReferences);
      setCategoryToDelete(null);
    },
    [newReferences],
  );

  return (
    <>
      {isCreatingOrDeletingReferences && <Loading />}

      <Drawer
        className={styles.groupReferencesDrawer}
        title={<Heading type="h3">{t("group-references.title")}</Heading>}
        isOpen={isOpen}
        onClose={onClose}
        heightGap={visibleHeaderPixels}
        closeOnOverlayClick
        footer={
          <div className={styles.groupReferencesDrawer_footer}>
            <Button variant="secondary" onClick={onClose}>
              {t("generals.cancel")}
            </Button>
            <Button
              disabled={
                !hasChanges ||
                isCreatingOrDeletingReferences ||
                hasHermesOrHermeslabReference.isFreeText
              }
              onClick={handleSaveChanges}
            >
              {t("generals.save-changes")}
            </Button>
          </div>
        }
      >
        <div className={styles.groupReferencesDrawer_content}>
          <div className={styles.groupReferencesDrawer_modality}>
            <Text className={styles.groupReferencesDrawer_modalityTitle} weight="medium">
              {t("group-references.modalities.title")}
            </Text>

            <ul className={styles.groupReferencesDrawer_modalities}>
              {referenceCategories.map(category => {
                const isDisabled = isModalityDisabled(category, isLaboratoryGroup);

                const modalityRadio = (
                  <RadioButton
                    labelRight={category?.name}
                    checked={selectedModality?.code === category?.code}
                    disabled={isDisabled}
                    onChange={() => !isDisabled && setSelectedModality(category)}
                  />
                );

                return (
                  <li key={category.id} className={styles.groupReferencesDrawer_modalityItem}>
                    {isDisabled ? (
                      <Tooltip
                        placement="bottom"
                        label={
                          <span className={styles.groupReferencesDrawer_modalityTooltip}>
                            {t("group-references.modalities.hermeslab-disabled-tooltip")}
                          </span>
                        }
                      >
                        {modalityRadio}
                      </Tooltip>
                    ) : (
                      modalityRadio
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {!!selectedModality && selectedModality?.code === FREE_TEXT_MODALITY_CODE && (
            <TextField
              label={t("group-references.free-text-reference.title")}
              placeholder={t("group-references.free-text-reference.placeholder")}
              value={freeTextReference}
              onChange={e => setFreeTextReference(e.target.value)}
              onKeyUp={handleSaveReferenceFromTextfield}
            />
          )}

          {!!selectedModality && selectedModality?.code !== FREE_TEXT_MODALITY_CODE && (
            <Tooltip
              visible={hasHermesOrHermeslabReference.hasReference ? undefined : false}
              label={
                <>
                  {t("group-references.notifications.group-selector.tooltip.title")}
                  <br />
                  {t("group-references.notifications.group-selector.tooltip.description")}
                </>
              }
            >
              <GroupExternalCrnSelector
                scenarioId={scenario}
                originId={origin}
                termCode={termCode}
                label={t("group-references.group-selector.label", {
                  modality: selectedModality?.name,
                })}
                placeholder={t("group-references.group-selector.placeholder")}
                disabled={hasHermesOrHermeslabReference.hasReference}
                onChange={option => handleSaveReferenceFromGroupExternalCrnSelector(option?.self)}
              />
            </Tooltip>
          )}

          {hasHermesOrHermeslabReference.isFreeText && (
            <CardNotification
              state="error"
              title={t("group-references.notifications.reference-invalid.title")}
              outlined
            >
              {t("group-references.notifications.reference-invalid.description")}
            </CardNotification>
          )}

          <div className={styles.groupReferencesDrawer_referencesContainer}>
            {!!newReferences?.length &&
              isOpen &&
              referencesByCategory?.map(({ category, values }, index) => (
                <div
                  key={`${category?.code}-${index}`}
                  className={styles.groupReferencesDrawer_references}
                >
                  <Heading type="h3" className={styles.groupReferencesDrawer_referencesTitle}>
                    {t("group-references.list.title", { category: category?.name })}

                    {values?.length > 1 && (
                      <Link iconLeft="trash" onClick={() => setCategoryToDelete(category)}>
                        {t("group-references.list.delete-all")}
                      </Link>
                    )}
                  </Heading>

                  <ul className={styles.groupReferencesDrawer_referencesList}>
                    {values?.map((reference, index) => (
                      <li
                        key={reference?.value}
                        className={styles.groupReferencesDrawer_referenceItem}
                      >
                        <Tag
                          className={styles.groupReferencesDrawer_referenceTag}
                          icon="close"
                          onIconClick={() => {
                            const updatedReferences = [...newReferences];
                            updatedReferences.splice(index, 1);
                            setNewReferences(updatedReferences);
                          }}
                          active={!!reference?.isNew}
                        >
                          {reference?.customLabel ?? reference?.value}
                        </Tag>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </Drawer>

      <Modal
        show={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        typeState="confirm"
        title={t("group-references.notifications.delete-all.title")}
        textButtonSecondary={t("generals.cancel")}
        textButtonPrincipal={t("group-references.notifications.delete-all.confirm-button")}
        onClickPrincipal={() => handleDeleteAllReferences(categoryToDelete)}
        onClickSecondary={() => setCategoryToDelete(null)}
      >
        <Text>{t("group-references.notifications.delete-all.description")}</Text>
      </Modal>
    </>
  );
};

export default GroupReferencesDrawer;
